import { ChannelDomainModel } from '../../../../domain/currently-on-tv-tray';
import { Channel } from '../../../../domain/channel';
import { TrayWithScheduling, TrayWithStringScheduling } from '../../../../domain/tray-common';
import { PlatformType } from 'osn-shared';
import { SchedulingType } from '../../../../shared/enums/scheduling-type.enum';
import { SchedulingControlViewModel } from '../../scheduling-control/scheduling-control-view-model';
import { TraySelectorType } from '../../tray-selector/tray-selector.enum';
import { ImageData, LocalizedDomainImages } from '../../../../domain/localized-domain-images';
import {
  AllPlatformLocalizedImagesViewModel,
  BackgroundImageViewModel,
} from '../../../../shared/components/image-upload-control/image-upload-view-model';
import { MarketingItemLayout } from '../../../../domain/domain-layouts';
import { MarketingTrayButton } from 'projects/osn-cxb/src/app/domain/marketing-tray-button';
import { MarketingButtonFormViewModel } from '../../marketing-button-control/marketing-button-form-view-model';

export const nameLocalizedMapperDomainToViewModel = <
  T extends { titleEnglish: string; titleArabic?: string; title?: { en: string; ar: string } }
>(
  tray: T
): { titleEnglish: string; titleArabic?: string; title?: { en: string; ar: string } } => {
  return {
    titleEnglish: tray.titleEnglish,
    titleArabic: tray.titleArabic,
    title: { en: tray.titleEnglish, ar: tray.titleArabic },
  };
};

export const commonMapperDomainToViewModel = <T extends { id: string }>(tray: T): { id: string } => {
  return {
    id: tray.id,
  };
};

const userTargetingMapper = <T extends { userSegmentIds: string[] }>(tray: T): { userSegmentIds: string[] } => {
  return {
    userSegmentIds: tray.userSegmentIds,
  };
};

const countryTargetingMapper = <T extends { countryIds: string[] }>(tray: T): { countryIds: string[] } => {
  return {
    countryIds: tray.countryIds,
  };
};

export const targetingMapper = <T extends { userSegmentIds: string[]; countryIds: string[] }>(
  tray: T
): { userSegmentIds: string[]; countryIds: string[] } => {
  return {
    ...userTargetingMapper(tray),
    ...countryTargetingMapper(tray),
  };
};

export const layoutMapper = <T extends { layout: TraySelectorType }>(tray: T): { layout: TraySelectorType } => {
  return {
    layout: tray.layout,
  };
};

const mapSchedulingToViewModel = <T extends Partial<TrayWithScheduling | TrayWithStringScheduling>>(
  tray: T
): { scheduling: SchedulingControlViewModel } => {
  return {
    scheduling: {
      schedulingType: tray.scheduledFrom || tray.scheduledTo ? SchedulingType.Schedule : SchedulingType.LiveWhenPublished,
      scheduledTo: tray.scheduledTo ? new Date(tray.scheduledTo).toISOString() : undefined,
      scheduledFrom: tray.scheduledFrom ? new Date(tray.scheduledFrom).toISOString() : undefined,
    },
  };
};

export const scheduledDateMapperDomainToViewModel = <T extends Partial<TrayWithScheduling>>(
  tray: T
): { scheduling: SchedulingControlViewModel } => mapSchedulingToViewModel(tray);

export const scheduledDateMapperApiModelToViewModel = <T extends Partial<TrayWithStringScheduling>>(
  tray: T
): { scheduling: SchedulingControlViewModel } => mapSchedulingToViewModel(tray);

export const displayServiceTypeIconMapper = <T extends { displayServiceTypeIcon: boolean }>(
  tray: T
): { displayServiceTypeIcon: boolean } => {
  return {
    displayServiceTypeIcon: tray.displayServiceTypeIcon,
  };
};

export const displayServiceTypeIconApiModelToViewModel = <T extends { displayServiceTypeIcon?: boolean }>(
  tray: T
): { displayServiceTypeIcon: boolean } => {
  return {
    displayServiceTypeIcon: !!tray?.displayServiceTypeIcon,
  };
};

export const platformMapper = <T extends { platformIds?: string[] }>(tray: T): { platformIds?: string[] } => {
  return {
    platformIds: [...tray.platformIds],
  };
};

export const mapSchedulingDomainToViewModel = (scheduling: TrayWithScheduling): SchedulingControlViewModel => {
  return {
    schedulingType: scheduling.scheduledFrom || scheduling.scheduledTo ? SchedulingType.Schedule : SchedulingType.LiveWhenPublished,
    scheduledFrom: scheduling.scheduledFrom ? new Date(scheduling.scheduledFrom).toISOString() : undefined,
    scheduledTo: scheduling.scheduledTo ? new Date(scheduling.scheduledTo).toISOString() : undefined,
  };
};

export const mapDomainImageToViewModelImage = (image: ImageData): BackgroundImageViewModel => {
  return {
    name: image.name,
    id: image.id,
    publicUrl: image.publicUrl,
  };
};

export const mapDomainImagesToPlatformLocalizedViewModelImage = <S extends Partial<AllPlatformLocalizedImagesViewModel>>(
  images: ImageData[]
): S => {
  const backgroundImagesVm = {};
  images.forEach((image) => {
    const platform = image.platformName ?? PlatformType.Web;
    if (!(platform in backgroundImagesVm)) {
      backgroundImagesVm[platform] = { en: undefined };
    }
    backgroundImagesVm[platform].en = mapDomainImageToViewModelImage(image);
  });
  return backgroundImagesVm as S;
};

export const mapMarketingItemLayoutToTraySelectorType = (layout: MarketingItemLayout): TraySelectorType => {
  switch (layout) {
    case MarketingItemLayout.CTA_LEFT:
      return TraySelectorType.MarketingCTALeft;
    case MarketingItemLayout.CTA_RIGHT:
      return TraySelectorType.MarketingCTARight;
    case MarketingItemLayout.EMAIL_CENTERED:
      return TraySelectorType.MarketingEmailCollectorCentered;
    case MarketingItemLayout.EMAIL_LEFT:
      return TraySelectorType.MarketingEmailCollectorLeft;
    case MarketingItemLayout.EMAIL_RIGHT:
      return TraySelectorType.MarketingEmailCollectorRight;
    case MarketingItemLayout.MULTIPLE_CONTENT_LANDSCAPE:
      return TraySelectorType.MarketingMultipleContentLandscape;
    case MarketingItemLayout.MULTIPLE_CONTENT_PORTRAIT:
      return TraySelectorType.MarketingMultipleContentPortrait;
    case MarketingItemLayout.DETAILED_PAGE_BANNER_LEFT:
      return TraySelectorType.DetailedPageBannerLeft;
    case MarketingItemLayout.DETAILED_PAGE_BANNER_RIGHT:
      return TraySelectorType.DetailedPageBannerRight;
    case MarketingItemLayout.DETAILED_PAGE_BANNER_CENTER:
      return TraySelectorType.DetailedPageBannerCenter;
    default:
      return undefined;
  }
};

export const mapChannelDomainModelToViewModel = (
  channel: ChannelDomainModel
): { id: string; order: number; name: string; channel: Channel } => {
  return {
    id: channel.id,
    order: channel.order,
    name: channel.channel.channelName,
    channel: {
      id: channel.channel.id,
      channelID: channel.channel.channelID,
      channelName: channel.channel.channelName,
      channelNumber: channel.channel.channelNumber,
    },
  };
};

export const mapDomainButtonToButtonViewModel = (button: MarketingTrayButton): MarketingButtonFormViewModel => {
  return {
    action: button.action,
    buttonTextEnglish: button.buttonTextEnglish,
    buttonTextArabic: button.buttonTextArabic,
    actionExternalUrl: button.buttonActionExternalUrl,
    actionPageTargetId: button.buttonActionPageTargetId,
    actionProgramTarget: button.buttonActionProgramTarget
      ? {
          availabilityFrom: button.buttonActionProgramTarget.availabilityFrom,
          availabilityTo: button.buttonActionProgramTarget.availabilityTo,
          id: button.buttonActionProgramTarget.id,
          title: button.buttonActionProgramTarget.title,
          episode: button.buttonActionProgramTarget.episode,
          season: button.buttonActionProgramTarget.season,
        }
      : undefined,
  };
};

export const mapLocalizedDomainImagesToPlatformLocalizedViewModelImage = <S extends Partial<AllPlatformLocalizedImagesViewModel>>(
  images: LocalizedDomainImages
): S => {
  let backgroundImagesVm = {};
  Object.keys(images).forEach((language) => {
    if (language === 'ar' || language === 'en') {
      backgroundImagesVm = images[language].reduce((imagesVm, image) => {
        const platform = image.platformName ?? PlatformType.Web;
        return {
          ...imagesVm,
          [platform]: {
            ...imagesVm[platform],
            [language]: mapDomainImageToViewModelImage(image),
          },
        };
      }, backgroundImagesVm);
    }
  });
  return backgroundImagesVm as S;
};
